import React, { useState } from 'react';
import { 
  MdHowToVote, MdEmojiEvents, MdMilitaryTech,
  MdLocalFireDepartment, MdAutoAwesome, MdCreate, MdInfo, MdShare, MdTrendingUp,
  MdPeople, MdClose, MdMonetizationOn
} from 'react-icons/md';
import { BadgeIcon } from './achievements/badgeData';
import { BadgeInfoModal } from './achievements/BadgeInfoModal';
import { BadgeShareModal } from './achievements/BadgeShareModal';
import { SeeAllBadgesModal } from './achievements/SeeAllBadgesModal';

function AchievementsBadges({ badges = [] }) {
  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAllBadgesModal, setShowAllBadgesModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const displayedEarnedBadges = earnedBadges.slice(0, 6);
  const displayedUnearnedBadges = unearnedBadges.slice(0, 3);
  const hasMoreBadges = badges.length > 9;

  const handleInfoClick = (badge, e) => {
    e.stopPropagation();
    setSelectedBadge(badge);
    setShowInfoModal(true);
  };

  const handleShareClick = (badge, e) => {
    e.stopPropagation();
    setSelectedBadge(badge);
    setShowShareModal(true);
  };

  return (
    <div className="card-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
          <MdMilitaryTech className="text-yellow-500 text-xl" />
          Badges & Achievements
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 font-rubik">
            {earnedBadges.length}/{badges.length}
          </span>
          {hasMoreBadges && (
            <button
              onClick={() => setShowAllBadgesModal(true)}
              className="text-sm text-primary hover:text-primary-dark font-medium font-rubik underline"
              data-testid="see-all-badges-btn"
            >
              See All
            </button>
          )}
        </div>
      </div>

      {/* Earned Badges */}
      {displayedEarnedBadges.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-rubik">Earned</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayedEarnedBadges.map((badge) => (
              <div
                key={badge.id}
                className="relative group p-3 rounded-lg border-2 bg-gradient-to-br from-white to-gray-50 hover:shadow-lg transition-all cursor-pointer"
                style={{ borderColor: badge.color + '40' }}
                data-testid={`badge-${badge.id}`}
              >
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => handleInfoClick(badge, e)}
                    className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    title="How to earn"
                    data-testid={`badge-info-${badge.id}`}
                  >
                    <MdInfo className="text-gray-500 text-sm" />
                  </button>
                  <button
                    onClick={(e) => handleShareClick(badge, e)}
                    className="w-6 h-6 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors"
                    title="Share achievement"
                    data-testid={`badge-share-${badge.id}`}
                  >
                    <MdShare className="text-blue-500 text-sm" />
                  </button>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: badge.color + '20' }}
                  >
                    <BadgeIcon icon={badge.icon} color={badge.color} size={28} earned={true} />
                  </div>
                  <p className="text-sm font-semibold text-gray-800 font-rubik leading-tight">{badge.name}</p>
                  <p className="text-xs text-gray-500 font-rubik mt-1 hidden group-hover:block">{badge.description}</p>
                </div>
                <div 
                  className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `linear-gradient(135deg, ${badge.color}10 0%, transparent 50%)` }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unearned Badges */}
      {displayedUnearnedBadges.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-rubik">Next to Unlock</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayedUnearnedBadges.map((badge) => (
              <div
                key={badge.id}
                className="relative group p-3 rounded-lg border border-gray-200 bg-gray-50 opacity-70"
                data-testid={`badge-locked-${badge.id}`}
              >
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => handleInfoClick(badge, e)}
                    className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                    title="How to earn"
                    data-testid={`badge-info-locked-${badge.id}`}
                  >
                    <MdInfo className="text-gray-500 text-sm" />
                  </button>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 bg-gray-200">
                    <BadgeIcon icon={badge.icon} color={badge.color} size={28} earned={false} />
                  </div>
                  <p className="text-sm font-semibold text-gray-500 font-rubik leading-tight">{badge.name}</p>
                  {badge.progress !== undefined && (
                    <div className="w-full mt-2">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ width: `${badge.progress}%`, backgroundColor: badge.color }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 font-rubik">{badge.progress}%</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {badges.length === 0 && (
        <div className="text-center py-6 text-gray-500 font-rubik">
          <MdEmojiEvents className="text-4xl mx-auto mb-2 text-gray-300" />
          <p>Start voting to earn badges!</p>
        </div>
      )}

      {showInfoModal && selectedBadge && (
        <BadgeInfoModal 
          badge={selectedBadge} 
          onClose={() => setShowInfoModal(false)} 
          onShare={handleShareClick} 
        />
      )}
      {showShareModal && selectedBadge && (
        <BadgeShareModal badge={selectedBadge} onClose={() => setShowShareModal(false)} />
      )}
      {showAllBadgesModal && (
        <SeeAllBadgesModal 
          badges={badges} 
          onClose={() => setShowAllBadgesModal(false)} 
          onBadgeClick={handleInfoClick} 
        />
      )}
    </div>
  );
}

function RankingCard({ ranking }) {
  const { rank, totalUsers, percentile } = ranking;
  
  const getRankColor = () => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    if (percentile >= 90) return '#10B981';
    return '#3B82F6';
  };

  const getRankLabel = () => {
    if (rank === 1) return '#1';
    if (rank === 2) return '#2';
    if (rank === 3) return '#3';
    return `#${rank}`;
  };

  return (
    <div className="card-container">
      <h3 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2 mb-4">
        <MdTrendingUp className="text-green-500 text-xl" />
        Your Ranking
      </h3>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
            style={{ backgroundColor: getRankColor() }}
            data-testid="user-rank"
          >
            {rank}
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-800 font-rubik">{getRankLabel()}</p>
            <p className="text-sm text-gray-500 font-rubik">out of {totalUsers.toLocaleString()} voters</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold font-rubik" style={{ color: getRankColor() }}>Top {100 - percentile}%</p>
          <p className="text-sm text-gray-500 font-rubik">Better than {percentile}% of voters</p>
        </div>
      </div>
      {rank > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 font-rubik">Keep voting to climb the ranks!</p>
        </div>
      )}
    </div>
  );
}

function PollPointsCard({ pollPoints, stats }) {
  const [showInfo, setShowInfo] = useState(false);
  const total = pollPoints?.total || 0;
  const breakdown = pollPoints?.breakdown || {};

  const scoreItems = [
    { label: 'Voting Points', value: breakdown.votingPoints || 0, icon: MdHowToVote, color: '#10B981', hint: '1 pt per poll answered' },
    { label: 'Streak Bonus', value: breakdown.streakBonus || 0, icon: MdLocalFireDepartment, color: '#F59E0B', hint: 'Bonus for fast answering' },
    { label: 'Creator Points', value: breakdown.pollCreatorPoints || 0, icon: MdCreate, color: '#3B82F6', hint: '10 pts per poll created' },
    { label: 'Popularity', value: breakdown.popularityPoints || 0, icon: MdPeople, color: '#8B5CF6', hint: '1 pt per unique respondent' },
    { label: 'Badge Bonus', value: breakdown.badgeBonus || 0, icon: MdMilitaryTech, color: '#EC4899', hint: 'From earned badges' },
  ];

  return (
    <div className="card-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
          <MdAutoAwesome className="text-emerald-500 text-xl" />
          Poll Points
        </h3>
        <button
          onClick={() => setShowInfo(true)}
          className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          title="How to earn points"
          data-testid="poll-points-info-btn"
        >
          <MdInfo className="text-gray-500 text-base" />
        </button>
      </div>
      <div className="text-center mb-6 py-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
        <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 font-rubik" data-testid="poll-points-total">
          {total.toLocaleString()}
        </p>
        <p className="text-sm text-gray-500 font-rubik mt-1">Total Points</p>
      </div>
      <div className="space-y-3">
        {scoreItems.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: item.color + '20' }}>
                <item.icon size={16} style={{ color: item.color }} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 font-rubik">{item.label}</p>
                <p className="text-xs text-gray-400 font-rubik">{item.hint}</p>
              </div>
            </div>
            <p className="text-sm font-semibold font-rubik" style={{ color: item.color }}>+{item.value}</p>
          </div>
        ))}
      </div>
      {stats?.currentStreakMultiplier > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-amber-600">
          <MdLocalFireDepartment className="text-xl animate-pulse" />
          <span className="font-semibold font-rubik">{stats.currentStreakMultiplier}x Streak Active!</span>
        </div>
      )}
      {stats?.votingStreak > 0 && !(stats?.currentStreakMultiplier > 1) && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-orange-500">
          <MdLocalFireDepartment className="text-xl" />
          <span className="font-semibold font-rubik">{stats.votingStreak} day streak!</span>
        </div>
      )}
      {(breakdown.pointsSpent || 0) > 0 && (
        <div className="mt-1 flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EF444420' }}>
              <MdMonetizationOn size={16} style={{ color: '#EF4444' }} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 font-rubik">Points Spent</p>
              <p className="text-xs text-gray-400 font-rubik">On custom polls</p>
            </div>
          </div>
          <p className="text-sm font-semibold font-rubik" style={{ color: '#EF4444' }} data-testid="poll-points-spent">-{breakdown.pointsSpent}</p>
        </div>
      )}

      {showInfo && <PollPointsInfoModal onClose={() => setShowInfo(false)} />}
    </div>
  );
}

function PollPointsInfoModal({ onClose }) {
  const infoItems = [
    {
      icon: MdHowToVote, color: '#10B981', title: 'Voting Points',
      desc: 'Earn 1 point for each poll question you answer. Changing your answer to a previously answered poll does not earn extra points.',
    },
    {
      icon: MdLocalFireDepartment, color: '#F59E0B', title: 'Streak Bonus',
      desc: 'Answer 5+ questions within 5 minutes to activate a 2x streak — each answer earns double points! Answer 25+ in 10 minutes for a 3x streak! Streaks end after 2 minutes of inactivity.',
    },
    {
      icon: MdCreate, color: '#3B82F6', title: 'Poll Creator Points',
      desc: 'Earn 10 points for every custom poll question you create.',
    },
    {
      icon: MdPeople, color: '#8B5CF6', title: 'Popularity Points',
      desc: 'Earn 1 point for every unique person that responds to one of your custom polls. Multiple answer changes by the same user do not count extra.',
    },
    {
      icon: MdMilitaryTech, color: '#EC4899', title: 'Badge Bonus',
      desc: 'Earn points from badges! Easy badges = 10 pts, Medium badges = 25 pts, Hard badges = 50 pts.',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="poll-points-info-modal"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800 font-rubik">How to Earn Poll Points</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            data-testid="poll-points-info-close"
          >
            <MdClose className="text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-5">
          {infoItems.map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: item.color + '15' }}>
                <item.icon size={20} style={{ color: item.color }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 font-rubik">{item.title}</p>
                <p className="text-xs text-gray-500 font-rubik mt-0.5 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold font-rubik transition-colors text-sm"
            data-testid="poll-points-info-got-it"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

export { AchievementsBadges, RankingCard, PollPointsCard };
